import { Capacitor } from '@capacitor/core';
import { RiRestartLine } from '@remixicon/react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../../../components/layout';
import { LiveChat } from '../../../components/LiveChat';
import TabsInLine from '../../../components/TabsInLine';
import { Games } from '../../../components/Tournaments/Games';
import ButtonBack from '../../../components/ui/buttonBack';
import { HeaderTournamentNew } from '../../../components/Widgets/HeaderTournamentNew';
import { useAuthContext } from '../../../contexts/Auth/AuthContext';
import { useEnrollContext } from '../../../contexts/Enroll/EnrollContext';
import { sentryCapture } from '../../../helpers/utils';
import { useApi } from '../../../hooks/useApi';
import { useModalBalanceAlertStore } from '../../../store/modalBalanceAlert';
import { useTokenGetStreamStore, useTokenStore } from '../../../store/token';
import { SentryError } from '../../../types/enums';
import { TournamentData } from '../../../types/types';
import { ResultsOptions } from '../Tournaments/ResultsOptions';
import { Awards } from './Awards';
import { Options } from './Option';
import { Participants } from './Participants';

export const TournamentById = () => {
	const { id } = useParams();
	const api = useApi();
	const { user } = useAuthContext();
	const {
		enroll,
		enrollOptions,
		optionSelection,
		initialSelectEnroll,
		enrollOption,
		SetOptionTournament,
		SetOptionSelection,
		SetTournament,
		openModalEnroll,
		depositReturnEnroll,
	} = useEnrollContext();
	const {
		urlAction,
		leagues,
		optionsSelect: optionSelected,
		clearEnrollsInfoModal,
	} = useModalBalanceAlertStore();
	const tokenGetStream = useTokenGetStreamStore(
		(state) => state.tokenGetStream
	);
	const token = useTokenStore((state: any) => state.token);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<TournamentData | null>(null);
	const [optionsSelect, setOptionsSelect] = useState<any>([]);
	const [countSelect, setCountSelect] = useState(0);

	useEffect(() => {
		fetchTournament();
	}, []);

	useEffect(() => {
		enrollOption(optionsSelect);
		setCountSelect(optionsSelect.length);
	}, [optionsSelect]);

	useEffect(() => {
		if (!data) return;

		if (enroll.length > 0 && countSelect === optionSelection) {
			if (!user) {
				return;
			}
			setTimeout(() => {
				openModalEnroll();
			}, 2000);
		}
	}, [enroll, enrollOptions, user]);

	const fetchTournament = async () => {
		if (!id) return;
		try {
			setLoading(true);
			const res = await api.getTournament(id, token);

			if (!res?.success) {
				throw new Error(res.message);
			}
			setData(res.data);
			SetTournament({
				tournamentId: id,
				tournamentName: res.data.title,
				tournamentStatus: res.data.tournamentStatus,
				tournamentEntryAmt: res.data.entryAmt,
				tournamentPrizeAmt: res.data.prizeAmt,
				tournamentPrizeAmtBonus: Number(res.data.prizeAmtBonus),
				tournamentMultipleEntry: res.data.multipleEntry,
				sharedUrl:
					Capacitor.getPlatform() === 'ios'
						? res.data.sharedUrlIOS
						: Capacitor.getPlatform() === 'android'
						? res.data.sharedUrlAndroid
						: res.data.sharedUrl,
				data: res.data,
			});
			SetOptionTournament(res.data.optionsTournament);
			SetOptionSelection(parseInt(res.data.optionTotal));

			if (urlAction) {
				setOptionsSelect(optionSelected);
				enrollOption(optionSelected);
				depositReturnEnroll(leagues);
				clearEnrollsInfoModal();
				openModalEnroll();
			} else {
				initialSelectEnroll({
					entryAmt: res.data.entryAmt,
					prizeAmt: res.data.prizeAmt || 0,
					prizeAmtBonus: Number(res.data.prizeAmtBonus) || 0,
				});
			}
		} catch (error: any) {
			sentryCapture(error, 'getTournament', SentryError.Error);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectOption = useCallback((newOption: any) => {
		setOptionsSelect((prevOptions: any) => {
			const existingOptionIndex = prevOptions.findIndex(
				(option: any) => option.optionId === newOption.optionId
			);
			if (existingOptionIndex !== -1) {
				if (newOption.delete) {
					return prevOptions.filter(
						(option: any) => option.optionId !== newOption.optionId
					);
				} else {
					const updatedOptions = [...prevOptions];
					updatedOptions[existingOptionIndex] = newOption;
					return updatedOptions;
				}
			} else {
				return [...prevOptions, newOption];
			}
		});
	}, []);

	const awardsRender =
		data && data.award
			? [
					{
						title: 'Premiação',
						renderPage: () => (
							<div className="px-3">
								<Awards
									award={data.award}
									tournamentStatus={data?.tournamentStatus}
									prizeAmt={data?.prizeAmt}
									registeredEnrolls={data?.registeredEnrolls}
								/>
							</div>
						),
					},
			  ]
			: [];
	const liveChatRender =
		id &&
		data &&
		data?.tournamentStatus !== 3 &&
		data?.tournamentStatus !== 2
			? [
					{
						title: 'Chat ao vivo',
						renderPage: () => (
							<LiveChat
								tournamentId={id}
								token={tokenGetStream}
							/>
						),
						notPadding: true,
					},
			  ]
			: [];
	return (
		<Layout key={id}>
			<div className="lg:max-w-screen-xl lg:mx-auto">
				<div className="px-3 items-center flex-row flex justify-between py-2 lg:max-w-screen-xl lg:mx-auto lg:pt-4">
					<ButtonBack classNameIcon="" />
					<p className="text-xl font-semibold text-custom-gray1 font-bahnschrift"></p>
					<div></div>
				</div>
				{loading ? (
					<div className="px-3 flex items-center justify-center">
						<RiRestartLine className="mr-2 h-4 w-4 animate-spin" />
					</div>
				) : (
					<div className="flex flex-col gap-2">
						<div className="px-3">
							{data && <HeaderTournamentNew data={data} />}
						</div>
						{data && (
							<TabsInLine
								initialPage="Jogos"
								render={[
									{
										title: 'Jogos',
										renderPage: () => (
											<div className="px-3">
												<Games
													eventsTournament={
														data.eventsTournament
													}
												/>
											</div>
										),
									},
									{
										title:
											data.tournamentStatus === 0
												? 'Perguntas'
												: 'Resultados',
										renderPage: () =>
											data.tournamentStatus === 0 ? (
												<div className="px-3">
													<Options
														data={data}
														countSelect={
															countSelect
														}
														optionsSelect={
															optionsSelect
														}
														handleSelectOption={
															handleSelectOption
														}
													/>
												</div>
											) : (
												<div className="px-3">
													<ResultsOptions
														data={data}
													/>
												</div>
											),
									},
									{
										title: 'Inscrições',
										renderPage: () => (
											<div className="px-3">
												<Participants
													tournamentStatus={
														data?.tournamentStatus
													}
													myEnrolls={data.myEnrolls}
													tournamentId={id}
												/>
											</div>
										),
									},
									...awardsRender,
									...liveChatRender,
								]}
							/>
						)}
					</div>
				)}
			</div>
		</Layout>
	);
};
